<script setup>
import {usePortalContentAndDesignStore} from "../../../../../../stores/partners/portalContentAndDesignStore";
import EditableResourceImageField
    from "../../../../portal-editable-fields/resources/EditableResourceImageField.vue";
import EditableTextField from "../../../../portal-editable-fields/EditableTextField.vue";
import EditableResourceTextEditorField
    from "../../../../portal-editable-fields/resources/EditableResourceTextEditorField.vue";
import {ref, computed} from "vue";

const props = defineProps({
    resource: {
        type: Object,
        required: true,
    },
});

const portalContentAndDesignStore = usePortalContentAndDesignStore();

const hoverMainContentTextContainer = ref(false);

const alignmentClass = computed(() => {
    switch (props.resource.alignment) {
        case 'center': return 'justify-center text-center';
        case 'right': return 'justify-end text-right';
        default: return 'justify-start text-left';
    }
});

const headerLayoutClass = computed(() => {
    const position = props.resource.main_image_position || 'left';
    const alignment = props.resource.alignment || 'left';

    if (position === 'top') {
        if (alignment === 'center') return 'flex flex-col items-center gap-4';
        if (alignment === 'right') return 'flex flex-col items-end gap-4';
        return 'flex flex-col items-start gap-4';
    }

    if (alignment === 'center') return 'flex flex-col items-center gap-4';
    return 'flex items-center gap-6';
});

const imageSizeClass = computed(() => {
    // Backwards compatibility: check for old retina resize setting
    if (props.resource.main_image_size === undefined && props.resource.main_image_retina_resize !== undefined) {
        return props.resource.main_image_retina_resize ? 'max-w-8' : 'max-w-16';
    }
    // New size setting
    const size = props.resource.main_image_size || 'medium';
    switch (size) {
        case 'small':
        case '@2x': return 'max-w-8';
        case 'large': return 'max-w-24';
        default: return 'max-w-16'; // medium
    }
});

</script>

<template>
    <div>
        <div :class="[headerLayoutClass, alignmentClass]"
             @mouseenter="hoverMainContentTextContainer = true"
             @mouseleave="hoverMainContentTextContainer = false"
             :id="resource.unique_id"
        >
                <editable-resource-image-field
                    resource-image-path="main_image"
                    :resource-content="resource"
                    :img-class="imageSizeClass"
                    img-name="main_image"
                    :show-add-image-button="hoverMainContentTextContainer"
                />

                <div class="space-y-1.5">
                    <editable-text-field
                        v-if="resource.title"
                        text-type="h3"
                        :class="['text-xl md:text-2xl font-semibold', portalContentAndDesignStore.getPageContentTextColorClass]"
                        v-model="resource.title"
                    />
                    <editable-resource-text-editor-field
                        v-if="resource.description"
                        :class="[
                            'partnero-page-content-section text-base md:text-lg text-opacity-80 space-y-1',
                            portalContentAndDesignStore.getPageContentTextColorClass
                        ]"
                        :resource="resource"
                        resource-field="description"
                    />
                </div>
            </div>

        <template v-if="resource.contents?.length">
            <div :class="[(resource.title || resource.description) ? 'mt-6' : '', 'flex flex-col gap-4']">
                <editable-resource-text-editor-field
                    v-for="content in resource.contents"
                    :key="content.unique_id"
                    :resource="content"
                    :class="[
                        'partnero-page-content-block text-base',
                        portalContentAndDesignStore.getPageContentTextColorClass
                    ]"
                    resource-field="text"
                />
            </div>
        </template>
    </div>
</template>
